import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { liveInstructionApi, learningHooksApi, googleDriveApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
}

type LiveLevel = 'define' | 'do' | 'explain' | 'apply';
type LiveMode = 'direct_instruction' | 'workshop' | 'review_revision' | 'apply_studio';
type PublishedState = 'draft' | 'published';

type ArtifactKind = 'key_points' | 'examples' | 'resources' | 'next_steps' | 'recording_link';

interface LiveArtifact {
  id: number;
  kind: ArtifactKind;
  content: string;
  links?: any[];
  published_state: PublishedState;
}

interface LiveSession {
  id: number;
  learning_hook: number;
  level: LiveLevel;
  mode: LiveMode;
  status: 'active' | 'ended';
  title: string;
  instruction_source_type?: string | null;
  instruction_source_id?: string | null;
  follow_mode?: string | null;
  artifacts?: LiveArtifact[];
}

interface SignalCounts {
  [key: string]: number;
}

const LEVELS: { value: LiveLevel; label: string }[] = [
  { value: 'define', label: 'Define' },
  { value: 'do', label: 'Do' },
  { value: 'explain', label: 'Explain' },
  { value: 'apply', label: 'Apply' },
];

const MODES: { value: LiveMode; label: string }[] = [
  { value: 'direct_instruction', label: 'Direct Instruction' },
  { value: 'workshop', label: 'Workshop / Work Time' },
  { value: 'review_revision', label: 'Review / Revision' },
  { value: 'apply_studio', label: 'Apply Studio' },
];

const ARTIFACT_KINDS: { kind: ArtifactKind; label: string }[] = [
  { kind: 'key_points', label: 'Key Points' },
  { kind: 'examples', label: 'Examples' },
  { kind: 'resources', label: 'Resources' },
  { kind: 'next_steps', label: 'What to do next' },
  { kind: 'recording_link', label: 'Recording link' },
];

export const LiveInstructionPanel: React.FC = () => {
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [selectedHookId, setSelectedHookId] = useState<number | null>(null);

  const [session, setSession] = useState<LiveSession | null>(null);

  const [startLevel, setStartLevel] = useState<LiveLevel>('define');
  const [startMode, setStartMode] = useState<LiveMode>('direct_instruction');
  const [startTitle, setStartTitle] = useState('');

  const [editLevel, setEditLevel] = useState<LiveLevel>('define');
  const [editMode, setEditMode] = useState<LiveMode>('direct_instruction');
  const [editTitle, setEditTitle] = useState('');

  const [artifactContentByKind, setArtifactContentByKind] = useState<Record<string, string>>({});
  const [artifactStateByKind, setArtifactStateByKind] = useState<Record<string, PublishedState>>({});

  const [signalsTotals, setSignalsTotals] = useState<SignalCounts>({});
  const [signalsLast2, setSignalsLast2] = useState<SignalCounts>({});
  const [aiSuggestion, setAiSuggestion] = useState('');

  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadGooglePicker = useCallback(async () => {
    if ((window as any).google?.picker) return;

    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[data-google-picker="1"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Google API')));
        return;
      }

      const s = document.createElement('script');
      s.src = 'https://apis.google.com/js/api.js';
      s.async = true;
      s.defer = true;
      s.setAttribute('data-google-picker', '1');
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Google API'));
      document.body.appendChild(s);
    });

    await new Promise<void>((resolve, reject) => {
      (window as any).gapi.load('picker', {
        callback: () => resolve(),
        ontimeout: () => reject(new Error('Failed to load Google Picker')),
        timeout: 10000,
      });
    });
  }, []);

  const fetchSignals = useCallback(async (sessionId: number) => {
    const res = await liveInstructionApi.getSignals(sessionId);
    setSignalsTotals(res?.data?.totals || {});
    setSignalsLast2(res?.data?.last_2_minutes || {});
  }, []);

  const selectedHookTitle = useMemo(() => {
    const h = hooks.find((x) => x.id === selectedHookId);
    return h?.title || '';
  }, [hooks, selectedHookId]);

  const hydrateFromSession = useCallback((s: LiveSession | null) => {
    setSession(s);
    setSuccess('');

    if (s) {
      setEditLevel(s.level);
      setEditMode(s.mode);
      setEditTitle(s.title || '');

      const nextContent: Record<string, string> = {};
      const nextState: Record<string, PublishedState> = {};
      for (const k of ARTIFACT_KINDS) {
        const found = (s.artifacts || []).find((a) => a.kind === k.kind);
        nextContent[k.kind] = found?.content || '';
        nextState[k.kind] = (found?.published_state as PublishedState) || 'draft';
      }
      setArtifactContentByKind(nextContent);
      setArtifactStateByKind(nextState);
    } else {
      setEditTitle('');
      setArtifactContentByKind({});
      setArtifactStateByKind({});
    }
  }, []);

  const fetchHooks = useCallback(async () => {
    const hooksResponse = await learningHooksApi.getAll();
    const hooksData = hooksResponse.data.learning_hooks || hooksResponse.data || [];
    const minimal = (Array.isArray(hooksData) ? hooksData : []).map((h: any) => ({
      id: h.id,
      title: h.title,
    }));
    setHooks(minimal);
    if (minimal.length > 0 && selectedHookId == null) {
      setSelectedHookId(minimal[0].id);
    }
  }, [selectedHookId]);

  const fetchSession = useCallback(async (hookId: number) => {
    const res = await liveInstructionApi.getHookSession(hookId);
    const s = (res?.data?.session || null) as LiveSession | null;
    hydrateFromSession(s);
  }, [hydrateFromSession]);

  const openSlidesPicker = useCallback(async () => {
    if (!session) return;

    setError('');
    setSuccess('');

    const devKey = (process.env.REACT_APP_GOOGLE_PICKER_API_KEY || '').trim();
    if (!devKey) {
      setError('Missing REACT_APP_GOOGLE_PICKER_API_KEY (Google Picker API key)');
      return;
    }

    try {
      await loadGooglePicker();
      const tokenRes = await googleDriveApi.accessToken();
      const accessToken = tokenRes?.data?.access_token;
      const pickerAppId = String(tokenRes?.data?.picker_app_id || '');
      if (!accessToken || !/^\d+$/.test(pickerAppId)) {
        setError('Google Picker credentials are incomplete');
        return;
      }

      const view = new (window as any).google.picker.DocsView().setIncludeFolders(false).setMimeTypes('application/vnd.google-apps.presentation');

      const picker = new (window as any).google.picker.PickerBuilder()
        .setDeveloperKey(devKey)
        .setOAuthToken(accessToken)
        .setAppId(pickerAppId)
        .setOrigin(window.location.origin)
        .addView(view)
        .setCallback(async (data: any) => {
          if (data.action === (window as any).google.picker.Action.PICKED) {
            const doc = data.docs && data.docs[0];
            if (doc?.id) {
              try {
                setWorking(true);
                await liveInstructionApi.setInstructionSource(session.id, { instruction_source_id: doc.id });
                await fetchSession(session.learning_hook);
                setSuccess('Slides attached');
              } catch (e: any) {
                setError(e?.response?.data?.error || 'Failed to attach slides');
              } finally {
                setWorking(false);
              }
            }
          }
        })
        .build();

      picker.setVisible(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to open Google Picker');
    }
  }, [fetchSession, loadGooglePicker, session]);

  useEffect(() => {
    const boot = async () => {
      setLoading(true);
      setError('');
      try {
        await fetchHooks();
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load hooks');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [fetchHooks]);

  useEffect(() => {
    const run = async () => {
      if (!selectedHookId) return;
      setError('');
      try {
        await fetchSession(selectedHookId);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load live session');
      }
    };
    run();
  }, [fetchSession, selectedHookId]);

  useEffect(() => {
    if (!session || session.status !== 'active') {
      setSignalsTotals({});
      setSignalsLast2({});
      return;
    }

    let mounted = true;
    let timer: any = null;

    const run = async () => {
      try {
        await fetchSignals(session.id);
      } catch (e) {
        if (!mounted) return;
      }
    };

    run();
    timer = setInterval(run, 8000);

    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, [fetchSignals, session]);

  const handleMicroAssist = async (actionType: string) => {
    if (!session) return;
    setError('');
    setSuccess('');
    setWorking(true);
    try {
      const res = await liveInstructionApi.aiMicroAssist(session.id, { action_type: actionType });
      setAiSuggestion(String(res?.data?.suggestion_text || ''));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to get suggestion');
    } finally {
      setWorking(false);
    }
  };

  const slidesPreviewUrl = useMemo(() => {
    if (!session?.instruction_source_id) return '';
    return `https://docs.google.com/presentation/d/${session.instruction_source_id}/preview`;
  }, [session?.instruction_source_id]);

  const slidesEditUrl = useMemo(() => {
    if (!session?.instruction_source_id) return '';
    return `https://docs.google.com/presentation/d/${session.instruction_source_id}/edit`;
  }, [session?.instruction_source_id]);

  const handleStart = async () => {
    if (!selectedHookId) return;
    setError('');
    setSuccess('');
    setWorking(true);
    try {
      const res = await liveInstructionApi.startHookSession(selectedHookId, {
        level: startLevel,
        mode: startMode,
        title: startTitle,
      });
      hydrateFromSession(res?.data?.session || null);
      setSuccess('Live session started');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to start session');
    } finally {
      setWorking(false);
    }
  };

  const handleUpdate = async () => {
    if (!session) return;
    setError('');
    setSuccess('');
    setWorking(true);
    try {
      const res = await liveInstructionApi.updateSession(session.id, {
        level: editLevel,
        mode: editMode,
        title: editTitle,
      });
      hydrateFromSession(res?.data?.session || null);
      setSuccess('Session updated');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to update session');
    } finally {
      setWorking(false);
    }
  };

  const handleEnd = async () => {
    if (!session) return;
    setError('');
    setSuccess('');
    const ok = window.confirm('End this live session? Students will no longer see it as live.');
    if (!ok) return;

    setWorking(true);
    try {
      const res = await liveInstructionApi.endSession(session.id);
      hydrateFromSession(res?.data?.session || null);
      setSuccess('Session ended');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to end session');
    } finally {
      setWorking(false);
    }
  };

  const handleSaveArtifact = async (kind: ArtifactKind) => {
    if (!session) return;
    setError('');
    setSuccess('');
    setWorking(true);
    try {
      await liveInstructionApi.upsertArtifact(session.id, {
        kind,
        content: artifactContentByKind[kind] || '',
        links: [],
        published_state: artifactStateByKind[kind] || 'draft',
      });
      await fetchSession(session.learning_hook);
      setSuccess('Saved');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save artifact');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg shadow p-6">Loading…</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Live Instruction</h2>
          <p className="text-gray-600 text-sm">Start a live session for a hook level. This never unlocks stages.</p>
        </div>
      </div>

      {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}
      {success && <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">{success}</div>}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Learning Hook</label>
        <select
          value={selectedHookId ?? ''}
          onChange={(e) => setSelectedHookId(Number(e.target.value) || null)}
          className="w-full border rounded px-3 py-2"
        >
          {hooks.map((h) => (
            <option key={h.id} value={h.id}>
              {h.title}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4 text-sm text-gray-700">
        {selectedHookTitle ? <span>Hook: <span className="font-medium">{selectedHookTitle}</span></span> : null}
      </div>

      {!session || session.status !== 'active' ? (
        <div className="border rounded p-4 bg-gray-50">
          <div className="font-medium text-gray-900">Start a live session</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Level</label>
              <select value={startLevel} onChange={(e) => setStartLevel(e.target.value as LiveLevel)} className="w-full border rounded px-2 py-2">
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mode</label>
              <select value={startMode} onChange={(e) => setStartMode(e.target.value as LiveMode)} className="w-full border rounded px-2 py-2">
                {MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title (optional)</label>
              <input value={startTitle} onChange={(e) => setStartTitle(e.target.value)} className="w-full border rounded px-2 py-2" />
            </div>
          </div>
          <div className="mt-3">
            <button
              onClick={handleStart}
              disabled={!selectedHookId || working}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              type="button"
            >
              Start session
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="border rounded p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">Session is live</div>
                <div className="text-xs text-gray-600">Session #{session.id}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleUpdate}
                  disabled={working}
                  className="bg-white border px-3 py-2 rounded hover:bg-gray-100 disabled:opacity-50"
                  type="button"
                >
                  Save session
                </button>
                <button
                  onClick={handleEnd}
                  disabled={working}
                  className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                  type="button"
                >
                  End
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Level</label>
                <select value={editLevel} onChange={(e) => setEditLevel(e.target.value as LiveLevel)} className="w-full border rounded px-2 py-2">
                  {LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Mode</label>
                <select value={editMode} onChange={(e) => setEditMode(e.target.value as LiveMode)} className="w-full border rounded px-2 py-2">
                  {MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full border rounded px-2 py-2" />
              </div>
            </div>
          </div>

          <div className="border rounded p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-gray-900">Slides</div>
                <div className="text-xs text-gray-600 mt-1">Attach Google Slides for students to follow along (read-only).</div>
              </div>
              <button
                onClick={openSlidesPicker}
                disabled={working}
                className="bg-white border px-3 py-2 rounded hover:bg-gray-100 disabled:opacity-50"
                type="button"
              >
                Choose Slides from Drive
              </button>
            </div>

            {session.instruction_source_id ? (
              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-700 truncate">Deck ID: {session.instruction_source_id}</div>
                  <a href={slidesEditUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline">
                    Open in Google Slides
                  </a>
                </div>
                <div className="mt-2 border rounded overflow-hidden" style={{ height: 360 }}>
                  <iframe
                    title="Slides preview"
                    src={slidesPreviewUrl}
                    className="w-full h-full"
                    allow="autoplay"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-3 text-xs text-gray-600">No Slides attached yet.</div>
            )}
          </div>

          <div className="border rounded p-4">
            <div className="font-medium text-gray-900">Student signals</div>
            <div className="text-xs text-gray-600 mt-1">Last 2 minutes and total for this session.</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs font-medium text-gray-700">Last 2 minutes</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div>👍 {signalsLast2.good || 0}</div>
                  <div>❓ {signalsLast2.confused || 0}</div>
                  <div>🔁 {signalsLast2.another_example || 0}</div>
                  <div>⏸ {signalsLast2.slow_down || 0}</div>
                </div>
              </div>
              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs font-medium text-gray-700">Total</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div>👍 {signalsTotals.good || 0}</div>
                  <div>❓ {signalsTotals.confused || 0}</div>
                  <div>🔁 {signalsTotals.another_example || 0}</div>
                  <div>⏸ {signalsTotals.slow_down || 0}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="border rounded p-4">
            <div className="font-medium text-gray-900">AI micro-assists</div>
            <div className="text-xs text-gray-600 mt-1">Suggestions only. Nothing is student-facing until you publish highlights.</div>

            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => handleMicroAssist('another_example')} disabled={working} className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50" type="button">
                Another example
              </button>
              <button onClick={() => handleMicroAssist('rephrase_grade_level')} disabled={working} className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50" type="button">
                Rephrase for grade level
              </button>
              <button onClick={() => handleMicroAssist('quick_check')} disabled={working} className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50" type="button">
                Quick check
              </button>
              <button onClick={() => handleMicroAssist('common_misconception')} disabled={working} className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50" type="button">
                Common misconception
              </button>
              <button onClick={() => handleMicroAssist('connect_to_apply')} disabled={working} className="bg-white border px-3 py-2 rounded text-sm hover:bg-gray-100 disabled:opacity-50" type="button">
                Connect to Apply
              </button>
            </div>

            {aiSuggestion ? (
              <div className="mt-3 border rounded p-3 bg-gray-50">
                <div className="text-xs font-medium text-gray-700">Suggestion</div>
                <div className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{aiSuggestion}</div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(aiSuggestion);
                        setSuccess('Copied');
                      } catch (e) {
                        setSuccess('');
                      }
                    }}
                    className="bg-white border px-3 py-1.5 rounded text-xs hover:bg-gray-100"
                  >
                    Copy
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="border rounded p-4">
            <div className="font-medium text-gray-900">Session artifacts</div>
            <div className="text-xs text-gray-600 mt-1">Draft artifacts are teacher-only. Published artifacts are visible to students.</div>

            <div className="space-y-4 mt-4">
              {ARTIFACT_KINDS.map((k) => (
                <div key={k.kind} className="border rounded p-3 bg-gray-50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-gray-900">{k.label}</div>
                    <div className="flex items-center gap-2">
                      <select
                        value={artifactStateByKind[k.kind] || 'draft'}
                        onChange={(e) => setArtifactStateByKind((prev) => ({ ...(prev || {}), [k.kind]: e.target.value as PublishedState }))}
                        className="border rounded px-2 py-1 text-xs"
                      >
                        <option value="draft">draft</option>
                        <option value="published">published</option>
                      </select>
                      <button
                        onClick={() => handleSaveArtifact(k.kind)}
                        disabled={working}
                        className="bg-white border px-2 py-1 rounded text-xs hover:bg-gray-100 disabled:opacity-50"
                        type="button"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={artifactContentByKind[k.kind] || ''}
                    onChange={(e) => setArtifactContentByKind((prev) => ({ ...(prev || {}), [k.kind]: e.target.value }))}
                    rows={4}
                    className="w-full border rounded px-3 py-2 text-sm mt-2"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
